/* Soho Match quiz. Vanilla JS, no dependencies.
   The recommendation engine (HairMatchEngine) is pure so it can run in node for tests. */
(function (root) {
  'use strict';

  /* ---------------------------------------------------------------- Engine */

  function normCode(raw, aliases) {
    var code = String(raw || '').split(':')[0].replace(/\s+/g, '').toUpperCase();
    code = code.replace(/^S0(\d{2})$/, 'S$1'); // S015 -> S15, S01 stays S01
    if (aliases && aliases[code]) code = aliases[code];
    return code;
  }

  function cleanShadeName(raw) {
    var parts = String(raw || '').split(':');
    var name = parts.slice(1).join(':').replace(/\s+/g, ' ').trim().replace(/^H\.\s*/i, '');
    return name.replace(/\b([a-z])/g, function (m) { return m.toUpperCase(); });
  }

  function isColorOption(name) { return /colou?r|shade/i.test(name || ''); }

  // Trim /products/{handle}.js into what the quiz needs. Safe to cache.
  function parseProduct(p, aliases) {
    var options = (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
    var colorIdx = options.findIndex(isColorOption);
    var productImage = p.featured_image || (p.images && p.images[0]) || '';
    var swatchSrcs = {};
    (p.variants || []).forEach(function (v) {
      var src = v.featured_image && (v.featured_image.src || v.featured_image);
      if (src) swatchSrcs[String(src).split('?')[0].replace(/^https?:/, '')] = true;
    });
    var gallery = (p.images || []).filter(function (src) {
      return !swatchSrcs[String(src).split('?')[0].replace(/^https?:/, '')];
    }).slice(0, 6);
    if (!gallery.length && productImage) gallery = [productImage];
    return {
      handle: p.handle,
      title: p.title,
      url: '/products/' + p.handle,
      image: productImage,
      gallery: gallery,
      description: p.description || '',
      options: options,
      hasColor: colorIdx > -1,
      variants: (p.variants || []).map(function (v) {
        var opts = [v.option1, v.option2, v.option3].filter(function (x) { return x != null; });
        var colorRaw = colorIdx > -1 ? opts[colorIdx] : null;
        return {
          id: v.id,
          available: !!v.available,
          price: v.price,
          compareAt: v.compare_at_price || null,
          options: opts,
          otherOptions: opts.filter(function (_, i) { return i !== colorIdx; }),
          code: colorRaw ? normCode(colorRaw, aliases) : null,
          shadeName: colorRaw ? cleanShadeName(colorRaw) : '',
          rooted: colorRaw ? /root/i.test(colorRaw) : false,
          image: (v.featured_image && (v.featured_image.src || v.featured_image)) || ''
        };
      })
    };
  }

  function familyOf(config, code) {
    var fams = config.shadeFamilies;
    for (var f in fams) {
      if (fams[f].map(function (c) { return normCode(c, config.shadeAliases); }).indexOf(code) > -1) return f;
    }
    return null;
  }

  function familyCodes(config, fam) {
    var seen = {};
    return (config.shadeFamilies[fam] || []).map(function (c) { return normCode(c, config.shadeAliases); })
      .filter(function (c) { if (seen[c]) return false; seen[c] = true; return true; });
  }

  function sortIndex(config, code) {
    var order = config.shadeSortOrder.map(function (c) { return normCode(c, config.shadeAliases); });
    var i = order.indexOf(code);
    return i === -1 ? 999 : i;
  }

  function inList(list, val) { return Array.isArray(list) && list.indexOf(val) > -1; }

  // Does a rule apply? ignoreFields lets the swatch step match before fiber/shade are known.
  function ruleMatches(rule, a, family, ignoreFields) {
    ignoreFields = ignoreFields || {};
    if (rule.goal && rule.goal !== a.goal) return false;
    if (rule.detail && !ignoreFields.detail && !inList(rule.detail, a.detail)) return false;
    if (rule.hairLength && !ignoreFields.hairLength && !inList(rule.hairLength, a.hairLength)) return false;
    if (rule.fiber && !ignoreFields.fiber && rule.fiber !== a.fiber) return false;
    if (rule.shadeFamily && !ignoreFields.shadeFamily && rule.shadeFamily !== family) return false;
    return true;
  }

  function ruleScore(rule) {
    return (rule.shadeFamily ? 8 : 0) + (rule.fiber ? 4 : 0) + (rule.detail ? 2 : 0) + (rule.hairLength ? 1 : 0);
  }

  function selectRule(config, a, family) {
    var best = null, bestScore = -1;
    config.rules.forEach(function (r) {
      if (!ruleMatches(r, a, family)) return;
      var s = ruleScore(r);
      if (s > bestScore) { best = r; bestScore = s; }
    });
    return best;
  }

  function overrideFor(rule, a) {
    return (rule.overrides || []).find(function (o) {
      return Object.keys(o.if || {}).every(function (k) { return inList(o.if[k], a[k]); });
    });
  }

  function candidatesFor(rule, a) {
    var ov = overrideFor(rule, a);
    return (ov ? ov.candidates : rule.candidates).slice();
  }

  // Pick the best available variant of a product in a shade.
  // opts.length: preferred non-color option value; opts.rooted: want the rooted version; opts.option: exact option value.
  function pickVariant(product, code, opts) {
    if (!product) return null;
    opts = opts || {};
    var pool = product.variants.filter(function (v) {
      if (!v.available) return false;
      if (!product.hasColor) return true;
      return v.code === code && v.rooted === !!opts.rooted;
    });
    if (!pool.length) return null;
    if (opts.option) {
      return pool.find(function (v) { return v.otherOptions.indexOf(opts.option) > -1; }) || null;
    }
    if (opts.length) {
      var byLen = pool.find(function (v) { return v.otherOptions.indexOf(opts.length) > -1; });
      if (byLen) return byLen;
    }
    // Avoid combined sets like "14 Inch & 20 Inch" unless nothing else is available.
    return pool.find(function (v) { return !v.otherOptions.some(function (o) { return /&/.test(o); }); }) || pool[0];
  }

  function availableCodes(product) {
    var out = {};
    if (!product || !product.hasColor) return out;
    product.variants.forEach(function (v) { if (v.available && v.code && !v.rooted) out[v.code] = true; });
    return out;
  }

  function closestCodeOnProduct(config, product, code, family) {
    var codes = familyCodes(config, family);
    var target = codes.indexOf(code);
    var avail = availableCodes(product);
    var best = null, bestDist = Infinity;
    codes.forEach(function (c, i) {
      if (!avail[c] || c === code) return;
      var d = Math.abs(i - target);
      if (d < bestDist || (d === bestDist && sortIndex(config, c) < sortIndex(config, best))) { best = c; bestDist = d; }
    });
    return best;
  }

  function resolveRule(config, rule, a, code, family, products) {
    var cands = candidatesFor(rule, a);
    var lengthValue = rule.lengthOption ? rule.lengthOption[a.hairLength] : null;
    var never = config.neverHero || [];
    var hero = null, shadeUsed = code, exact = true;

    function tryList(list, allowNever) {
      for (var i = 0; i < list.length; i++) {
        var h = list[i];
        if (!allowNever && never.indexOf(h) > -1) continue;
        var v = pickVariant(products[h], code, { length: lengthValue });
        if (v) return { handle: h, variant: v };
      }
      return null;
    }

    hero = tryList(cands, false) || tryList(cands, true);

    // Closest shade in the same family on the first candidate that has one.
    if (!hero) {
      var closestList = cands.concat(config.fallbacks || []);
      for (var i = 0; i < closestList.length && !hero; i++) {
        var p = products[closestList[i]];
        if (!p || !p.hasColor) continue;
        var alt = closestCodeOnProduct(config, p, code, family);
        if (alt) {
          var v = pickVariant(p, alt, { length: lengthValue });
          if (v) { hero = { handle: closestList[i], variant: v }; shadeUsed = alt; exact = false; }
        }
      }
    }
    if (!hero) {
      var fb = tryList(config.fallbacks || [], true);
      if (fb) hero = fb;
    }
    if (!hero) return { rule: rule, hero: null, alternates: [], bundle: null, shadeUsed: code, exactShade: false };

    // Bundle
    var bundle = null;
    if (rule.bundleVariant) {
      var bv = pickVariant(products[rule.bundleVariant.handle], shadeUsed, { option: rule.bundleVariant.option });
      if (bv) bundle = { handle: rule.bundleVariant.handle, variant: bv, mode: rule.bundleVariant.handle === hero.handle ? 'upgrade' : 'add' };
    } else if (rule.bundle && rule.bundle !== hero.handle) {
      var bvar = pickVariant(products[rule.bundle], shadeUsed, {});
      if (bvar) bundle = { handle: rule.bundle, variant: bvar, mode: inList(rule.bundleIncludes, hero.handle) ? 'upgrade' : 'add' };
    }

    // Alternates: next candidates in the list, then fallbacks.
    var alternates = [];
    var skip = {}; skip[hero.handle] = true; if (bundle) skip[bundle.handle] = true;
    cands.concat(config.fallbacks || []).forEach(function (h) {
      if (alternates.length >= 2 || skip[h]) return;
      var v = pickVariant(products[h], shadeUsed, { length: lengthValue });
      if (v) { alternates.push({ handle: h, variant: v }); skip[h] = true; }
    });

    // Rooted option on the hero (only when a rooted version of this exact shade exists)
    var rootedVariant = pickVariant(products[hero.handle], shadeUsed, { rooted: true, length: lengthValue });

    return {
      rule: rule, hero: hero, alternates: alternates, bundle: bundle,
      shadeUsed: shadeUsed, exactShade: exact, rootedVariant: rootedVariant || null
    };
  }

  function recommend(config, answers, products) {
    var a = Object.assign({}, answers);
    var code = normCode(a.shade, config.shadeAliases);
    var family = familyOf(config, code) || a.family;
    var grey = family === 'grey';
    if (grey && config.greyForcesFutura) a.fiber = 'futura';

    var fibers;
    if (a.fiber === 'futura' || a.fiber === 'human') fibers = [a.fiber];
    else if (a.priority === 'natural') fibers = ['human', 'futura'];
    else fibers = ['futura', 'human'];

    var first = null, seen = {};
    for (var i = 0; i < fibers.length; i++) {
      var trial = Object.assign({}, a, { fiber: fibers[i] });
      var rule = selectRule(config, trial, family);
      if (!rule || seen[rule.id]) continue;
      seen[rule.id] = true;
      var res = resolveRule(config, rule, trial, code, family, products);
      res.fiberUsed = fibers[i];
      if (res.hero && res.exactShade) { first = res; break; }
      if (!first || (!first.hero && res.hero)) first = res;
    }
    if (!first) first = { rule: null, hero: null, alternates: [], bundle: null, shadeUsed: code, exactShade: false };
    first.family = family;
    first.greyNote = grey;
    first.shadeRequested = code;
    return first;
  }

  // Every handle the quiz may need for a goal (candidates, overrides, bundles, fallbacks).
  function handlesForGoal(config, goal) {
    var set = {};
    config.rules.forEach(function (r) {
      if (r.goal !== goal) return;
      r.candidates.forEach(function (h) { set[h] = 1; });
      (r.overrides || []).forEach(function (o) { o.candidates.forEach(function (h) { set[h] = 1; }); });
      if (r.bundle) set[r.bundle] = 1;
      if (r.bundleVariant) set[r.bundleVariant.handle] = 1;
    });
    (config.fallbacks || []).forEach(function (h) { set[h] = 1; });
    return Object.keys(set);
  }

  // Shades to offer on the swatch step: available in at least one candidate for the answers so far.
  function swatchShades(config, a, products, family) {
    var handles = {};
    config.rules.forEach(function (r) {
      if (!ruleMatches(r, a, family, { fiber: true, shadeFamily: true })) return;
      if (r.shadeFamily && r.shadeFamily !== family) return;
      if (family === 'grey' && config.greyForcesFutura && r.fiber === 'human') return;
      candidatesFor(r, a).forEach(function (h) { handles[h] = 1; });
    });
    var codes = familyCodes(config, family);
    var found = {};
    Object.keys(handles).forEach(function (h) {
      var p = products[h];
      if (!p || !p.hasColor) return;
      p.variants.forEach(function (v) {
        if (!v.available || v.rooted || codes.indexOf(v.code) === -1) return;
        var f = found[v.code] || (found[v.code] = { code: v.code, names: {}, image: '' });
        found[v.code].names[v.shadeName] = (found[v.code].names[v.shadeName] || 0) + 1;
        if (!f.image && v.image) f.image = v.image;
      });
    });
    return Object.keys(found).map(function (c) {
      var names = found[c].names;
      var name = Object.keys(names).sort(function (x, y) { return names[y] - names[x]; })[0];
      return { code: c, name: name, image: found[c].image };
    }).sort(function (x, y) { return sortIndex(config, x.code) - sortIndex(config, y.code); });
  }

  var Engine = {
    normCode: normCode, cleanShadeName: cleanShadeName, parseProduct: parseProduct, familyOf: familyOf,
    selectRule: selectRule, recommend: recommend, handlesForGoal: handlesForGoal, swatchShades: swatchShades,
    pickVariant: pickVariant
  };

  if (typeof module !== 'undefined' && module.exports) { module.exports = Engine; return; }
  root.HairMatchEngine = Engine;

  /* -------------------------------------------------------------------- UI */

  var CACHE_TTL = 10 * 60 * 1000;
  var STORAGE_KEY = 'sohoMatch';

  // Simple line icons (stroke uses currentColor).
  function svg(body, vb) {
    return '<svg viewBox="' + (vb || '0 0 48 48') + '" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + body + '</svg>';
  }
  var ICONS = {
    check: svg('<path d="M5 12l5 5 9-10"/>', '0 0 24 24'),
    back: svg('<path d="M15 5l-7 7 7 7"/>', '0 0 24 24'),
    restart: svg('<path d="M4 12a8 8 0 1 0 3-6.2"/><path d="M4 4v5h5"/>', '0 0 24 24'),
    swap: svg('<path d="M4 8h13l-3-3M20 16H7l3 3"/>', '0 0 24 24'),
    returns: svg('<path d="M4 12a8 8 0 1 0 8-8"/><path d="M4 4v5h5"/><path d="M12 8v4l3 2"/>', '0 0 24 24'),
    shield: svg('<path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z"/><path d="M8.5 12l2.5 2.5 4.5-5"/>', '0 0 24 24'),
    chat: svg('<path d="M4 5h16v11H9l-5 4z"/>', '0 0 24 24'),
    spark: svg('<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6"/>', '0 0 24 24')
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function imgUrl(src, width) {
    if (!src) return '';
    var u = src.indexOf('//') === 0 ? 'https:' + src : src;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function cleanHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html || '');
    tpl.content.querySelectorAll('script, style, iframe, object, embed, form, link, meta').forEach(function (n) { n.remove(); });
    tpl.content.querySelectorAll('*').forEach(function (n) {
      Array.prototype.slice.call(n.attributes).forEach(function (a) {
        if (/^on/i.test(a.name) || a.name === 'style' || /^\s*javascript:/i.test(a.value)) n.removeAttribute(a.name);
      });
    });
    return tpl.innerHTML;
  }

  function track(name, data) {
    data = data || {};
    try { if (root.Shopify && root.Shopify.analytics && root.Shopify.analytics.publish) root.Shopify.analytics.publish(name, data); } catch (e) {}
    try { if (typeof root.fbq === 'function') root.fbq('trackCustom', name, data); } catch (e) {}
    try { (root.dataLayer = root.dataLayer || []).push(Object.assign({ event: name }, data)); } catch (e) {}
  }

  function storeGet(store, key) { try { return JSON.parse(root[store].getItem(key)); } catch (e) { return null; } }
  function storeSet(store, key, val) { try { root[store].setItem(key, JSON.stringify(val)); } catch (e) {} }

  function HairMatchQuiz(el) {
    this.el = el;
    this.config = JSON.parse(document.getElementById('HairMatchConfig').textContent);
    this.settings = JSON.parse(el.querySelector('[data-hmq-settings]').textContent);
    this.copy = this.config.copy;
    this.answers = {};
    this.products = {};
    this.loading = {};
    this.history = [];
    this.startedAt = 0;
    this.email = '';
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: (root.Shopify && root.Shopify.currency && root.Shopify.currency.active) || 'USD'
    });
    this.screen = el.querySelector('[data-hmq-screen]');
    this.progress = el.querySelector('[data-hmq-progress]');
    this.progressBar = el.querySelector('[data-hmq-progress-bar]');
    this.live = el.querySelector('[data-hmq-live]');
    this.restartBtn = el.querySelector('[data-hmq-restart]');
    this.bind();
    var saved = storeGet('localStorage', STORAGE_KEY);
    if (saved && saved.answers && saved.answers.shade) this.showWelcomeBack(saved);
    else this.go('intro', true);
  }

  HairMatchQuiz.prototype.steps = function () {
    var a = this.answers, self = this;
    return [
      { id: 'goal' },
      { id: 'detail', when: function () { return a.goal && a.goal !== 'length'; } },
      { id: 'hairLength', when: function () { return a.goal !== 'updo' && a.goal !== 'bangs'; } },
      { id: 'family' },
      { id: 'shade' },
      { id: 'fiber', when: function () { return a.goal !== 'updo' && a.family !== 'grey'; } },
      { id: 'experience' },
      { id: 'priority' },
      { id: 'email' },
      { id: 'phone', when: function () { return self.emailSubmitted; } },
      { id: 'results' }
    ].filter(function (s) { return !s.when || s.when(); });
  };

  HairMatchQuiz.prototype.nextStep = function (id) {
    var steps = this.steps();
    var i = steps.findIndex(function (s) { return s.id === id; });
    return steps[i + 1] ? steps[i + 1].id : 'results';
  };

  HairMatchQuiz.prototype.bind = function () {
    var self = this;
    this.pointerAt = 0;
    this.el.addEventListener('pointerdown', function () { self.pointerAt = Date.now(); });
    this.el.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-hmq-rooted]')) { self.toggleRooted(t.checked); return; }
      if (!t.matches('input[type="radio"][data-hmq-key]')) return;
      self.setAnswer(t.getAttribute('data-hmq-key'), t.value);
      var next = self.el.querySelector('[data-hmq-next]');
      if (next) next.disabled = false;
      // Auto-advance on tap. Keyboard users move with arrows, then press Enter or Next.
      if (Date.now() - self.pointerAt < 1000) setTimeout(function () { self.advance(); }, 220);
    });
    this.el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('input[type="radio"][data-hmq-key]')) {
        e.preventDefault();
        if (!e.target.checked) { e.target.checked = true; self.setAnswer(e.target.getAttribute('data-hmq-key'), e.target.value); }
        self.advance();
      }
    });
    this.el.addEventListener('click', function (e) {
      var b = e.target.closest('[data-hmq-action]');
      if (!b) return;
      var act = b.getAttribute('data-hmq-action');
      if (act === 'start') { self.start(); }
      else if (act === 'next') { self.advance(); }
      else if (act === 'back') { self.back(); }
      else if (act === 'skip-email') { e.preventDefault(); track('quiz_skip_email', {}); self.go('results'); }
      else if (act === 'skip-phone') { e.preventDefault(); self.go('results'); }
      else if (act === 'retake') { e.preventDefault(); self.retake(); }
      else if (act === 'add') { self.closeDetails(); self.addToCart(b); }
      else if (act === 'details') {
        if (self.openDetails(b.getAttribute('data-handle'), Number(b.getAttribute('data-variant')), b)) e.preventDefault();
      }
      else if (act === 'close-details') { self.closeDetails(); }
      else if (act === 'gallery-prev' && self.galleryGo) { self.galleryGo(-1); }
      else if (act === 'gallery-next' && self.galleryGo) { self.galleryGo(1); }
      else if (act === 'gallery-go' && self.galleryGo) { self.galleryGo(0, Number(b.getAttribute('data-index'))); }
      else if (act === 'photo') {
        if (root.GorgiasChat && typeof root.GorgiasChat.open === 'function') { e.preventDefault(); root.GorgiasChat.open(); }
      }
    });
    var dlg = this.el.querySelector('[data-hmq-modal]');
    if (dlg) {
      // Click on the dimmed backdrop closes the popup.
      dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
      dlg.addEventListener('close', function () {
        document.documentElement.classList.remove('hmq-lock');
        if (self.detailsOpener && document.contains(self.detailsOpener)) self.detailsOpener.focus();
      });
    }
    this.el.addEventListener('submit', function (e) {
      var f = e.target;
      if (f.matches('[data-hmq-email-form]')) { e.preventDefault(); self.submitEmail(f); }
      if (f.matches('[data-hmq-phone-form]')) { e.preventDefault(); self.submitPhone(f); }
    });
  };

  HairMatchQuiz.prototype.setAnswer = function (key, val) {
    var a = this.answers;
    if (a[key] === val) return;
    a[key] = val;
    // Clear answers that no longer fit after an upstream change.
    if (key === 'goal') { delete a.detail; if (val === 'updo' || val === 'bangs') delete a.hairLength; delete a.shade; this.prefetch(); }
    if (key === 'family') delete a.shade;
    if (key === 'detail' || key === 'hairLength') delete a.shade;
  };

  HairMatchQuiz.prototype.start = function () {
    this.answers = {};
    this.history = [];
    this.emailSubmitted = false;
    this.startedAt = Date.now();
    track('quiz_start', {});
    this.go('goal');
  };

  HairMatchQuiz.prototype.retake = function () {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    this.start();
  };

  HairMatchQuiz.prototype.advance = function () {
    var id = this.current;
    var key = id;
    if (['goal', 'detail', 'hairLength', 'family', 'shade', 'fiber', 'experience', 'priority'].indexOf(id) > -1) {
      if (!this.answers[key]) return;
      var stepNo = this.steps().findIndex(function (s) { return s.id === id; }) + 1;
      track('quiz_step', { step: stepNo, step_name: id, answer: this.answers[key] });
    }
    this.go(this.nextStep(id));
  };

  HairMatchQuiz.prototype.back = function () {
    var prev = this.history.pop();
    if (prev) this.go(prev, true);
  };

  HairMatchQuiz.prototype.go = function (id, isBack) {
    if (this.current) this.interacted = true;
    if (!isBack && this.current && this.current !== id) this.history.push(this.current);
    this.current = id;
    this.render();
  };

  /* ------------------------------------------------------------ Data */

  HairMatchQuiz.prototype.fetchProduct = function (handle) {
    var self = this;
    if (this.products[handle]) return Promise.resolve(this.products[handle]);
    if (this.loading[handle]) return this.loading[handle];
    var cached = storeGet('sessionStorage', 'hmq:p2:' + handle);
    if (cached && Date.now() - cached.t < CACHE_TTL) { this.products[handle] = cached.p; return Promise.resolve(cached.p); }
    var root_ = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    var showRatings = !this.config.ratings || this.config.ratings.show !== false;
    var ratingReq = !showRatings ? Promise.resolve(null) : fetch(root_ + 'products/' + handle + '?view=hmq-data')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (t) { try { return JSON.parse(t); } catch (e) { return null; } })
      .catch(function () { return null; });
    this.loading[handle] = fetch(root_ + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(handle + ' ' + r.status); return r.json(); })
      .then(function (raw) {
        return ratingReq.then(function (rating) {
          var p = Engine.parseProduct(raw, self.config.shadeAliases);
          p.rating = rating && Number(rating.count) > 0 ? { value: Number(rating.rating), scale: Number(rating.scale) || 5, count: Number(rating.count) } : null;
          self.products[handle] = p;
          storeSet('sessionStorage', 'hmq:p2:' + handle, { t: Date.now(), p: p });
          return p;
        });
      })
      .catch(function () { return null; });
    return this.loading[handle];
  };

  HairMatchQuiz.prototype.prefetch = function () {
    var self = this;
    if (!this.answers.goal) return Promise.resolve();
    return Promise.all(Engine.handlesForGoal(this.config, this.answers.goal).map(function (h) { return self.fetchProduct(h); }));
  };

  /* ---------------------------------------------------------- Render */

  HairMatchQuiz.prototype.render = function () {
    var id = this.current, self = this;
    var steps = this.steps();
    var idx = steps.findIndex(function (s) { return s.id === id; });
    var showProgress = idx > -1 && id !== 'results';
    this.progress.hidden = !showProgress;
    if (this.restartBtn) this.restartBtn.hidden = id === 'intro';
    if (showProgress) {
      var pct = Math.round(((idx + 1) / steps.length) * 100);
      this.progressBar.style.width = pct + '%';
      this.progress.setAttribute('aria-valuenow', pct);
    }
    var html;
    switch (id) {
      case 'intro': html = this.renderIntro(); break;
      case 'family': case 'shade':
        html = this.renderLoading();
        this.prefetch().then(function () { if (self.current === id) self.paint(id === 'family' ? self.renderFamily() : self.renderShade()); });
        break;
      case 'email': html = this.renderEmail(); break;
      case 'phone': html = this.renderPhone(); break;
      case 'results':
        html = this.renderLoading();
        this.prefetch().then(function () { if (self.current === 'results') self.paint(self.renderResults()); });
        break;
      default: html = this.renderQuestion(id);
    }
    this.paint(html);
  };

  HairMatchQuiz.prototype.paint = function (html) {
    this.screen.innerHTML = html;
    // Move focus only after the shopper has interacted, never on first page load.
    if (!this.interacted) return;
    var focusEl = this.screen.querySelector('[data-hmq-focus]');
    if (focusEl) focusEl.focus({ preventScroll: true });
    var top = this.el.getBoundingClientRect().top + root.scrollY - 80;
    if (root.scrollY > top) root.scrollTo({ top: top, behavior: 'smooth' });
  };

  HairMatchQuiz.prototype.renderLoading = function () {
    return '<div class="hmq-loading" role="status"><span class="hmq-spinner" aria-hidden="true"></span>' +
      '<p tabindex="-1" data-hmq-focus>Finding your best match...</p></div>';
  };

  HairMatchQuiz.prototype.renderIntro = function () {
    var s = this.settings;
    return '<div class="hmq-intro">' +
      (s.intro_image ? '<img class="hmq-intro__img" src="' + esc(imgUrl(s.intro_image, 900)) + '" alt="" width="900" height="600">' : '') +
      '<h2 class="hmq-title" tabindex="-1" data-hmq-focus>' + esc(s.intro_heading) + '</h2>' +
      '<p class="hmq-sub">' + esc(s.intro_sub) + '</p>' +
      '<button type="button" class="button button--primary hmq-btn" data-hmq-action="start">' + esc(s.start_label) + '</button>' +
      '<p class="hmq-intro__meta">Takes about 60 seconds. Free shade swaps on every order.</p>' +
      '</div>';
  };

  HairMatchQuiz.prototype.questionDef = function (id) {
    var q = this.copy.questions;
    if (id === 'detail') return q.detail[this.answers.goal];
    return q[id];
  };

  HairMatchQuiz.prototype.backButton = function () {
    return '<button type="button" class="hmq-back" data-hmq-action="back">' + ICONS.back + ' Back</button>';
  };

  HairMatchQuiz.prototype.fieldset = function (id, legend, helper, cards, extra) {
    var answered = !!this.answers[id];
    return '<form class="hmq-step" onsubmit="return false">' + this.backButton() +
      '<fieldset class="hmq-fieldset">' +
      '<legend class="hmq-legend" tabindex="-1" data-hmq-focus>' + esc(legend) + '</legend>' +
      (helper ? '<p class="hmq-helper">' + esc(helper) + '</p>' : '') +
      '<div class="hmq-cards hmq-cards--' + id + '">' + cards + '</div>' +
      '</fieldset>' + (extra || '') +
      '<button type="button" class="button button--primary hmq-btn hmq-next" data-hmq-action="next" data-hmq-next' + (answered ? '' : ' disabled') + '>Next</button>' +
      '</form>';
  };

  HairMatchQuiz.prototype.card = function (key, opt, idx, imgSrc) {
    var id = 'hmq-' + key + '-' + idx;
    var checked = this.answers[key] === opt.value ? ' checked' : '';
    var media = imgSrc ? '<img class="hmq-card__img" src="' + esc(imgUrl(imgSrc, 400)) + '" alt="" width="200" height="200" loading="lazy">' : '';
    return '<label class="hmq-card" for="' + id + '">' +
      '<input class="hmq-radio" type="radio" id="' + id + '" name="hmq-' + key + '" value="' + esc(opt.value) + '" data-hmq-key="' + key + '"' + checked + '>' +
      '<span class="hmq-card__body">' + media +
      '<span class="hmq-card__text"><span class="hmq-card__label">' + esc(opt.label) + '</span>' +
      (opt.helper ? '<span class="hmq-card__helper">' + esc(opt.helper) + '</span>' : '') + '</span>' +
      '<span class="hmq-card__check">' + ICONS.check + '</span>' +
      '</span></label>';
  };

  HairMatchQuiz.prototype.renderQuestion = function (id) {
    var def = this.questionDef(id), self = this;
    var images = id === 'goal' ? Object.assign({}, this.settings.goal_default_images || {}, this.settings.goal_images || {}) : {};
    var cards = def.options.map(function (o, i) { return self.card(id, o, i, images[o.value]); }).join('');
    return this.fieldset(id, def.legend, def.helper, cards);
  };

  HairMatchQuiz.prototype.renderFamily = function () {
    var def = this.copy.questions.family, self = this;
    var opts = def.options.filter(function (o) {
      return Engine.swatchShades(self.config, self.answers, self.products, o.value).length > 0;
    });
    var cards = opts.map(function (o, i) { return self.card('family', o, i); }).join('');
    return this.fieldset('family', def.legend, def.helper, cards);
  };

  HairMatchQuiz.prototype.renderShade = function () {
    var def = this.copy.questions.shade, self = this;
    var shades = Engine.swatchShades(this.config, this.answers, this.products, this.answers.family);
    this.shadeNames = this.shadeNames || {};
    shades.forEach(function (s) { self.shadeNames[s.code] = s.name; });
    var cards = shades.map(function (s, i) {
      return self.card('shade', { value: s.code, label: s.code + ' ' + s.name }, i, s.image);
    }).join('');
    var photo = '<p class="hmq-photo"><a href="' + esc(this.settings.photo_match_url || '/pages/contact-us') + '" data-hmq-action="photo">' + esc(def.photoLink) + '</a></p>';
    return this.fieldset('shade', def.legend, null, cards, photo);
  };

  HairMatchQuiz.prototype.renderEmail = function () {
    var s = this.settings;
    var sub = esc(s.email_sub).replace('{offer_text}', '<span class="hmq-offer">' + esc(s.offer_text) + '</span>');
    return '<div class="hmq-step hmq-email">' + this.backButton() +
      '<h2 class="hmq-title" tabindex="-1" data-hmq-focus>' + esc(s.email_heading) + '</h2>' +
      '<p class="hmq-sub">' + sub + '</p>' +
      '<form class="hmq-form" data-hmq-email-form novalidate>' +
      '<label class="hmq-field-label" for="hmq-email">Email</label>' +
      '<input class="hmq-input" id="hmq-email" type="email" name="email" placeholder="Your email" autocomplete="email" inputmode="email" required aria-describedby="hmq-email-error">' +
      '<div class="hmq-hp" aria-hidden="true"><label for="hmq-website">Website</label><input id="hmq-website" type="text" name="website" tabindex="-1" autocomplete="off"></div>' +
      '<p class="hmq-error" id="hmq-email-error" role="alert" hidden>Please enter a valid email address.</p>' +
      '<button type="submit" class="button button--primary hmq-btn">' + esc(s.email_button) + '</button>' +
      '</form>' +
      '<p class="hmq-skip"><a href="#" data-hmq-action="skip-email">Skip, show my results</a></p>' +
      '</div>';
  };

  HairMatchQuiz.prototype.renderPhone = function () {
    var s = this.settings;
    return '<div class="hmq-step hmq-email">' +
      '<h2 class="hmq-title" tabindex="-1" data-hmq-focus>' + esc(s.phone_heading) + '</h2>' +
      '<p class="hmq-sub">' + esc(s.phone_sub) + '</p>' +
      '<form class="hmq-form" data-hmq-phone-form novalidate>' +
      '<label class="hmq-field-label" for="hmq-phone">Mobile number (optional)</label>' +
      '<input class="hmq-input" id="hmq-phone" type="tel" name="phone" placeholder="Mobile number" autocomplete="tel" inputmode="tel" aria-describedby="hmq-phone-consent hmq-phone-error">' +
      '<p class="hmq-consent" id="hmq-phone-consent">' + esc(s.sms_consent) + '</p>' +
      '<p class="hmq-error" id="hmq-phone-error" role="alert" hidden>Please enter a valid mobile number.</p>' +
      '<button type="submit" class="button button--primary hmq-btn">' + esc(s.phone_button) + '</button>' +
      '</form>' +
      '<p class="hmq-skip"><a href="#" data-hmq-action="skip-phone">Skip, show my results</a></p>' +
      '</div>';
  };

  /* --------------------------------------------------------- Email */

  HairMatchQuiz.prototype.tagsFor = function (rec) {
    var a = this.answers;
    var t = ['quiz-match', 'quiz-goal-' + a.goal];
    if (a.detail) t.push('quiz-detail-' + a.detail);
    t.push('quiz-shade-' + a.shade, 'quiz-family-' + a.family);
    t.push('quiz-fiber-' + (a.family === 'grey' || a.goal === 'updo' ? 'futura' : (a.fiber || 'either')));
    if (a.experience) t.push('quiz-exp-' + a.experience);
    if (a.priority) t.push('quiz-priority-' + a.priority);
    if (rec && rec.hero) t.push('quiz-hero-' + rec.hero.handle);
    return t.join(', ');
  };

  // Posts the native Shopify customer form without leaving the page.
  HairMatchQuiz.prototype.postCustomer = function (fields) {
    var form = this.el.querySelector('[data-hmq-customer-form]');
    if (!form) return Promise.resolve(false);
    form.target = 'hmq-sink';
    form.querySelector('[name="contact[email]"]').value = fields.email;
    form.querySelector('[name="contact[tags]"]').value = fields.tags;
    var phone = form.querySelector('[name="contact[phone]"]');
    if (phone) { phone.value = fields.phone || ''; phone.disabled = !fields.phone; }
    var data = new FormData(form);
    return fetch(form.action, { method: 'POST', body: data, credentials: 'same-origin' })
      .then(function (r) {
        // Bot protection may answer with a challenge page. Fall back to a real submit into a hidden frame.
        if (!r.ok || /\/challenge/.test(r.url)) { form.submit(); }
        return true;
      })
      .catch(function () { try { form.submit(); } catch (e) {} return false; });
  };

  HairMatchQuiz.prototype.submitEmail = function (f) {
    var self = this;
    var email = f.email.value.trim();
    var err = f.querySelector('.hmq-error');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { err.hidden = false; f.email.setAttribute('aria-invalid', 'true'); f.email.focus(); return; }
    err.hidden = true;
    var tooFast = (Date.now() - this.startedAt) / 1000 < (this.settings.min_seconds || 8);
    var bot = f.website && f.website.value;
    this.email = email;
    this.emailSubmitted = true;
    track('quiz_email_submit', {});
    try { if (typeof root.fbq === 'function') root.fbq('track', 'Lead'); } catch (e) {}
    // Suppress site sign-up popups for 7 days.
    try { localStorage.setItem('promo-bar-subscribed-' + location.host, JSON.stringify({ subscribed: true })); } catch (e) {}
    document.cookie = 'soho_quiz_subscribed=1; path=/; max-age=' + 7 * 24 * 3600 + '; SameSite=Lax';
    if (bot || tooFast) { this.go('phone'); return; }
    this.prefetch().then(function () {
      var rec = Engine.recommend(self.config, self.answers, self.products);
      self.lastTags = self.tagsFor(rec);
      self.postCustomer({ email: email, tags: self.lastTags });
    });
    this.go('phone');
  };

  HairMatchQuiz.prototype.submitPhone = function (f) {
    var raw = f.phone.value.trim();
    var digits = raw.replace(/[^\d+]/g, '');
    var err = f.querySelector('.hmq-error');
    if (!raw) { this.go('results'); return; }
    if (digits.replace(/\D/g, '').length < 10) { err.hidden = false; f.phone.setAttribute('aria-invalid', 'true'); f.phone.focus(); return; }
    var phone = digits.charAt(0) === '+' ? digits : (digits.length === 10 ? '+1' + digits : '+' + digits);
    this.postCustomer({ email: this.email, phone: phone, tags: (this.lastTags || this.tagsFor(null)) + ', quiz-sms-optin' });
    this.go('results');
  };

  /* ------------------------------------------------------- Results */

  HairMatchQuiz.prototype.recapLine = function () {
    var a = this.answers, r = this.copy.recap, parts = [];
    var goalMap = r[a.goal] || {};
    var g = goalMap[a.detail] || goalMap._;
    if (g) parts.push(g);
    if (a.hairLength && r.hairLength[a.hairLength]) parts.push(r.hairLength[a.hairLength]);
    var shade = a.shade + (this.shadeNames && this.shadeNames[a.shade] ? ' ' + this.shadeNames[a.shade] : '');
    var last = 'love ' + shade;
    if (!parts.length) return 'Because you love ' + shade + '.';
    if (parts.length === 1) return 'Because ' + parts[0] + ' and ' + last + '.';
    return 'Because ' + parts.slice(0, -1).join(', ') + ', ' + parts[parts.length - 1] + ', and ' + last + '.';
  };

  HairMatchQuiz.prototype.whyBullets = function (rec) {
    var w = this.copy.why, a = this.answers, h = rec.hero.handle, list;
    if ((this.config.auraHandles || []).indexOf(h) > -1) list = w.aura.slice();
    else list = (w[a.goal] || w.fullness).slice(0, 2);
    var human = (this.config.humanHairHandles || []).indexOf(h) > -1;
    list.push(human ? w.human : w.futura);
    return list.slice(0, 3);
  };

  HairMatchQuiz.prototype.price = function (cents) { return this.money.format(cents / 100); };

  HairMatchQuiz.prototype.variantLabel = function (p, v) {
    if (!p.hasColor) return '';
    var other = v.otherOptions.filter(function (o) { return o && o !== 'Default Title'; });
    return v.code + ' ' + v.shadeName + (other.length ? ' / ' + other.join(' / ') : '');
  };

  // label + optional price ("Add to cart  ·  $200.00") or a trailing plus for compact cards.
  HairMatchQuiz.prototype.addButton = function (ids, type, handle, label, cls, price, plus) {
    var text = esc(label) + (price != null ? ' <span aria-hidden="true">&middot;</span> ' + this.price(price) : '');
    return '<button type="button" class="button button--primary hmq-btn ' + (cls || '') + '" data-hmq-action="add" data-ids="' + ids.join(',') +
      '" data-type="' + type + '" data-handle="' + esc(handle) + '"><span data-hmq-btn-label>' + text + '</span>' +
      (plus ? '<span class="hmq-plus" aria-hidden="true">+</span>' : '') + '</button>';
  };

  HairMatchQuiz.prototype.heroCard = function (rec) {
    var p = this.products[rec.hero.handle], v = rec.hero.variant;
    var img = p.image || v.image;
    var compare = v.compareAt && v.compareAt > v.price ? '<s class="hmq-compare">' + this.price(v.compareAt) + '</s> ' : '';
    var rooted = rec.rootedVariant ?
      '<label class="hmq-toggle"><input type="checkbox" data-hmq-rooted> <span>Prefer a rooted look?</span></label>' : '';
    var trust = [['swap', 'Free shade swap'], ['returns', '60-day returns'], ['shield', 'Warranty-backed'], ['chat', '24/7 customer care']];
    return '<article class="hmq-hero" data-hmq-hero>' +
      '<p class="hmq-hero__banner">Free shade swaps, always</p>' +
      '<div class="hmq-hero__row">' +
      '<div class="hmq-hero__media"><img data-hmq-hero-img src="' + esc(imgUrl(img, 600)) + '" alt="' + esc(p.title + ' in ' + v.shadeName) + '" width="600" height="750"></div>' +
      '<div class="hmq-hero__info">' +
      '<p class="hmq-hero__label">Your match</p>' +
      '<h3 class="hmq-hero__title">' + esc(p.title) + '</h3>' + this.stars(p) +
      '<p class="hmq-hero__shade" data-hmq-hero-shade>' + esc(this.variantLabel(p, v)) + '</p>' +
      '<p class="hmq-price">' + compare + '<span data-hmq-hero-price>' + this.price(v.price) + '</span></p>' +
      '<p class="hmq-sezzle">or 4 interest-free payments with Sezzle</p>' +
      '</div></div>' +
      rooted +
      '<div class="hmq-hero__cta" data-hmq-hero-add>' + this.addButton([v.id], 'hero', p.handle, 'Add to cart', '', v.price) + '</div>' +
      '<p class="hmq-hero__links">' + this.detailsLink(p, v, 'data-hmq-hero-link') + '</p>' +
      '<ul class="hmq-trust" aria-label="Our promise">' +
      trust.map(function (t) { return '<li>' + ICONS[t[0]] + '<span>' + t[1] + '</span></li>'; }).join('') +
      '</ul></article>';
  };

  HairMatchQuiz.prototype.miniCard = function (item, type) {
    var p = this.products[item.handle], v = item.variant;
    return '<article class="hmq-mini">' +
      '<img src="' + esc(imgUrl(p.image || v.image, 400)) + '" alt="' + esc(p.title) + '" width="400" height="500" loading="lazy">' +
      '<h4 class="hmq-mini__title">' + esc(p.title) + '</h4>' + this.stars(p) +
      '<p class="hmq-mini__shade">' + esc(this.variantLabel(p, v)) + '</p>' +
      '<p class="hmq-price">' + this.price(v.price) + '</p>' +
      this.addButton([v.id], type, p.handle, 'Add', 'hmq-btn--sm', null, true) +
      this.detailsLink(p, v) + '</article>';
  };

  // Star rating from Judge.me data. Hidden when ratings are off or below the configured minimums.
  HairMatchQuiz.prototype.stars = function (p) {
    var cfg = this.config.ratings || {};
    var r = p.rating;
    if (cfg.show === false || !r || r.count < (cfg.minCount || 1) || r.value < (cfg.minRating || 0)) return '';
    var pct = Math.max(0, Math.min(100, (r.value / r.scale) * 100));
    var five = '<svg viewBox="0 0 90 17" aria-hidden="true" focusable="false">' + [0, 1, 2, 3, 4].map(function (i) {
      return '<path transform="translate(' + (i * 18.4) + ' 0)" fill="currentColor" d="M8.5 0.8l2.3 4.9 5.3.6-3.9 3.6 1.1 5.3-4.8-2.7-4.8 2.7 1.1-5.3L.9 6.3l5.3-.6z"/>';
    }).join('') + '</svg>';
    var label = 'Rated ' + r.value.toFixed(1) + ' out of ' + r.scale;
    return '<p class="hmq-stars" role="img" aria-label="' + label + '"><span class="hmq-stars__track">' +
      '<span class="hmq-stars__off">' + five + '</span><span class="hmq-stars__on" style="width:' + pct.toFixed(1) + '%">' + five + '</span></span>' +
      '<span aria-hidden="true"><strong>' + r.value.toFixed(1) + '</strong></span></p>';
  };

  // Real link (works without JS); opens the product popup when clicked.
  HairMatchQuiz.prototype.detailsLink = function (p, v, attr) {
    return '<a class="hmq-details" href="' + esc(p.url + '?variant=' + v.id) + '" data-hmq-action="details" data-handle="' + esc(p.handle) +
      '" data-variant="' + v.id + '"' + (attr ? ' ' + attr : '') + '>View details</a>';
  };

  // Product popup: photos, shade, price, add to cart and the product description.
  HairMatchQuiz.prototype.openDetails = function (handle, variantId, opener) {
    var p = this.products[handle];
    if (!p) return false;
    var v = p.variants.find(function (x) { return x.id === variantId; }) || p.variants[0];
    var dlg = this.el.querySelector('[data-hmq-modal]');
    if (!dlg || typeof dlg.showModal !== 'function') return false;
    var shots = p.gallery.map(function (src, i) {
      return '<img src="' + esc(imgUrl(src, 900)) + '" alt="' + esc(p.title + ' photo ' + (i + 1)) + '" width="900" height="1125"' + (i ? ' loading="lazy"' : '') + '>';
    }).join('');
    var compare = v.compareAt && v.compareAt > v.price ? '<s class="hmq-compare">' + this.price(v.compareAt) + '</s> ' : '';
    dlg.innerHTML = '<div class="hmq-modal__inner">' +
      '<button type="button" class="hmq-icon-btn hmq-modal__close" data-hmq-action="close-details" aria-label="Close">' +
      svg('<path d="M6 6l12 12M18 6L6 18"/>', '0 0 24 24') + '</button>' +
      '<div class="hmq-modal__media">' +
      '<div class="hmq-modal__gallery" tabindex="0" aria-label="Product photos" data-hmq-gallery>' + shots + '</div>' +
      (p.gallery.length > 1 ?
        '<button type="button" class="hmq-gal-btn hmq-gal-btn--prev" data-hmq-action="gallery-prev" aria-label="Previous photo">' + svg('<path d="M15 5l-7 7 7 7"/>', '0 0 24 24') + '</button>' +
        '<button type="button" class="hmq-gal-btn hmq-gal-btn--next" data-hmq-action="gallery-next" aria-label="Next photo">' + svg('<path d="M9 5l7 7-7 7"/>', '0 0 24 24') + '</button>' : '') +
      '</div>' +
      (p.gallery.length > 1 ? '<div class="hmq-dots">' + p.gallery.map(function (_, i) {
        return '<button type="button" class="hmq-dot" data-hmq-action="gallery-go" data-index="' + i + '" aria-label="Show photo ' + (i + 1) + '"' + (i === 0 ? ' aria-current="true"' : '') + '></button>';
      }).join('') + '</div>' : '') +
      '<div class="hmq-modal__body">' +
      '<h2 class="hmq-modal__title" id="hmq-modal-title">' + esc(p.title) + '</h2>' + this.stars(p) +
      (p.hasColor ? '<p class="hmq-hero__shade">' + esc(this.variantLabel(p, v)) + '</p>' : '') +
      '<p class="hmq-price">' + compare + this.price(v.price) + '</p>' +
      '<p class="hmq-sezzle">or 4 interest-free payments with Sezzle</p>' +
      '<div class="hmq-modal__cta">' + (v.available ? this.addButton([v.id], 'details', p.handle, 'Add to cart', '', v.price) : '') + '</div>' +
      '<div class="hmq-modal__desc rte">' + cleanHtml(p.description) + '</div>' +
      '<p class="hmq-hero__links"><a class="hmq-details" href="' + esc(p.url + '?variant=' + v.id) + '">See full product page</a></p>' +
      '</div></div>';
    dlg.setAttribute('aria-labelledby', 'hmq-modal-title');
    this.detailsOpener = opener || null;
    dlg.showModal();
    document.documentElement.classList.add('hmq-lock');
    // Always open at the top: photos first, close button focused without scrolling.
    var closeBtn = dlg.querySelector('.hmq-modal__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
    dlg.scrollTop = 0;
    requestAnimationFrame(function () { dlg.scrollTop = 0; });
    this.setupGallery(dlg.querySelector('[data-hmq-gallery]'));
    track('quiz_view_details', { handle: handle });
    return true;
  };

  // Photo gallery: touch swipe (native), arrows, dots, keyboard, and click-and-drag for mouse users.
  HairMatchQuiz.prototype.setupGallery = function (gal) {
    if (!gal) return;
    var dlg = gal.closest('[data-hmq-modal]');
    var count = gal.children.length;
    var idx = 0, busy = null, settle = null, drag = null;
    var dots = function () {
      dlg.querySelectorAll('.hmq-dot').forEach(function (d, n) {
        if (n === idx) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
    };
    // Programmatic moves turn snapping off until the smooth scroll lands, so the browser cannot re-snap mid-way.
    var go = function (i) {
      idx = Math.max(0, Math.min(count - 1, i));
      dots();
      gal.classList.add('is-moving');
      gal.scrollTo({ left: idx * gal.clientWidth, behavior: 'smooth' });
      clearTimeout(busy);
      busy = setTimeout(function () { gal.classList.remove('is-moving'); busy = null; }, 450);
    };
    this.galleryGo = function (dir, index) { go(index != null ? index : idx + dir); };
    // Native touch swipes: read the position once scrolling settles.
    gal.addEventListener('scroll', function () {
      if (busy || drag) return;
      clearTimeout(settle);
      settle = setTimeout(function () { idx = Math.round(gal.scrollLeft / Math.max(1, gal.clientWidth)); dots(); }, 120);
    }, { passive: true });
    gal.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(idx - 1); }
    });
    gal.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: gal.scrollLeft };
      gal.classList.add('is-moving');
      gal.setPointerCapture(e.pointerId);
    });
    gal.addEventListener('pointermove', function (e) {
      if (drag) gal.scrollLeft = drag.left - (e.clientX - drag.x);
    });
    var end = function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      drag = null;
      go(Math.abs(dx) > gal.clientWidth * 0.15 ? idx + (dx < 0 ? 1 : -1) : idx);
    };
    gal.addEventListener('pointerup', end);
    gal.addEventListener('pointercancel', end);
    gal.addEventListener('dragstart', function (e) { e.preventDefault(); });
  };

  HairMatchQuiz.prototype.closeDetails = function () {
    var dlg = this.el.querySelector('[data-hmq-modal]');
    if (dlg && dlg.open) dlg.close();
  };

  HairMatchQuiz.prototype.bundleBlock = function (rec) {
    if (!rec.bundle) return '';
    var b = rec.bundle, p = this.products[b.handle], hv = rec.hero.variant;
    var body;
    if (b.mode === 'upgrade') {
      body = '<h3 class="hmq-h3">Upgrade to the set</h3>' + this.miniCardBody(p, b.variant, this.price(b.variant.price)) +
        this.addButton([b.variant.id], 'bundle', p.handle, 'Add the set', 'hmq-btn--outline', b.variant.price);
    } else {
      body = '<h3 class="hmq-h3">Complete the look</h3>' + this.miniCardBody(p, b.variant, this.price(b.variant.price)) +
        this.addButton([hv.id, b.variant.id], 'bundle', p.handle, 'Add both', 'hmq-btn--outline', hv.price + b.variant.price);
    }
    return '<section class="hmq-bundle">' + body + '</section>';
  };

  HairMatchQuiz.prototype.miniCardBody = function (p, v, price) {
    var shade = this.variantLabel(p, v);
    return '<div class="hmq-bundle__item"><img src="' + esc(imgUrl(p.image || v.image, 300)) + '" alt="" width="120" height="120" loading="lazy">' +
      '<div><p class="hmq-mini__title">' + esc(p.title) + '</p>' + this.stars(p) + (shade ? '<p class="hmq-mini__shade">' + esc(shade) + '</p>' : '') +
      '<p class="hmq-price">' + price + '</p></div></div>';
  };

  HairMatchQuiz.prototype.renderResults = function () {
    var a = this.answers, s = this.settings;
    var rec = Engine.recommend(this.config, a, this.products);
    this.rec = rec;
    if (!rec.hero) {
      return '<div class="hmq-results"><h2 class="hmq-title" tabindex="-1" data-hmq-focus>' + esc(s.results_heading) + '</h2>' +
        '<p class="hmq-sub">We could not find a match in stock right now. Our team can help you pick the right piece.</p>' +
        '<p><a class="button button--primary hmq-btn" href="' + esc(s.photo_match_url || '/pages/contact-us') + '">Talk to our team</a></p>' +
        '<p class="hmq-skip"><a href="#" data-hmq-action="retake">Retake the quiz</a></p></div>';
    }
    if (!this.shadeNames || !this.shadeNames[a.shade]) {
      var hv = rec.hero.variant;
      this.shadeNames = this.shadeNames || {};
      if (rec.exactShade && hv.shadeName) this.shadeNames[a.shade] = hv.shadeName;
    }
    storeSet('localStorage', STORAGE_KEY, { answers: a, heroHandle: rec.hero.handle, variantId: rec.hero.variant.id, timestamp: Date.now(), shadeName: this.shadeNames[a.shade] || '' });
    track('quiz_result_view', { hero: rec.hero.handle, rule: rec.rule ? rec.rule.id : null, exact_shade: rec.exactShade });

    var notes = '';
    if (!rec.exactShade) notes += '<p class="hmq-note">Your exact shade is restocking. Here is the closest match, and shade swaps are free.</p>';
    if (rec.greyNote) notes += '<p class="hmq-note">Our grey shades come in Futura fiber, made with cuticle for a realistic look.</p>';

    var why = '<section class="hmq-why"><h3 class="hmq-h3">Why it\'s your match</h3><ul>' +
      this.whyBullets(rec).map(function (b) { return '<li>' + ICONS.check + '<span>' + esc(b) + '</span></li>'; }).join('') + '</ul></section>';

    var howto = '';
    var howUrl = (s.howto_links || {})[a.goal];
    if (a.experience === 'first' && howUrl) {
      howto = '<section class="hmq-howto"><h3 class="hmq-h3">New to hair pieces?</h3>' +
        '<p><a class="hmq-details" href="' + esc(howUrl) + '">How to wear it</a></p></section>';
    }

    var alts = rec.alternates.length ? '<section class="hmq-alts"><h3 class="hmq-h3">Also a great match</h3><div class="hmq-alts__grid">' +
      rec.alternates.map(function (it) { return this.miniCard(it, 'alternate'); }, this).join('') + '</div></section>' : '';

    var heading = this.welcomeBack ? 'Welcome back, here\'s your match' : s.results_heading;
    return '<div class="hmq-results">' +
      '<p class="hmq-results__intro">' + (this.welcomeBack ? 'Good to see you again!' : 'You\'re all set!') + '</p>' +
      '<h2 class="hmq-title" tabindex="-1" data-hmq-focus>' + esc(heading) + '</h2>' +
      '<p class="hmq-sub">' + esc(this.recapLine()) + '</p>' + notes +
      this.heroCard(rec) + why + howto + this.bundleBlock(rec) + alts +
      '<p class="hmq-skip"><a href="#" data-hmq-action="retake">Retake the quiz</a></p>' +
      '</div>';
  };

  HairMatchQuiz.prototype.showWelcomeBack = function (saved) {
    this.answers = saved.answers;
    this.welcomeBack = true;
    this.shadeNames = {};
    if (saved.shadeName) this.shadeNames[saved.answers.shade] = saved.shadeName;
    this.current = 'results';
    this.render();
  };

  HairMatchQuiz.prototype.toggleRooted = function (on) {
    var rec = this.rec; if (!rec || !rec.rootedVariant) return;
    var p = this.products[rec.hero.handle];
    var v = on ? rec.rootedVariant : rec.hero.variant;
    var hero = this.el.querySelector('[data-hmq-hero]');
    hero.querySelector('[data-hmq-hero-shade]').textContent = this.variantLabel(p, v);
    hero.querySelector('[data-hmq-hero-price]').textContent = this.price(v.price);
    var link = hero.querySelector('[data-hmq-hero-link]');
    link.href = p.url + '?variant=' + v.id; link.setAttribute('data-variant', v.id);
    hero.querySelector('[data-hmq-hero-add]').innerHTML = this.addButton([v.id], 'hero', p.handle, 'Add to cart', '', v.price);
  };

  /* ---------------------------------------------------------- Cart */

  HairMatchQuiz.prototype.addToCart = function (btn) {
    var ids = btn.getAttribute('data-ids').split(',').map(Number);
    var type = btn.getAttribute('data-type');
    var label = btn.innerHTML;
    btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = 'Adding...';
    var drawer = document.querySelector('cart-drawer');
    var sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    try {
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        sectionIds = drawer.getSectionsToRender().map(function (s) { return s.id; });
      }
    } catch (e) {}
    var root_ = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    var self = this;
    fetch(root_ + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: ids.map(function (id) { return { id: id, quantity: 1 }; }),
        sections: sectionIds.join(','), sections_url: root.location.pathname
      })
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body.description || res.body.message || 'Could not add to cart');
        track('quiz_add_to_cart', { type: type, handle: btn.getAttribute('data-handle'), variant_ids: ids });
        btn.textContent = 'Added';
        self.live.textContent = 'Added to cart.';
        var opened = false;
        if (drawer && typeof drawer.renderContents === 'function' && res.body.sections) {
          try {
            var state = { id: ids[0], sections: res.body.sections, items: res.body.items };
            drawer.renderContents(state);
            if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
            opened = true;
          } catch (e) { opened = false; }
        }
        if (!opened) root.location.href = root_ + 'cart';
        setTimeout(function () { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = label; }, 2000);
      })
      .catch(function (err) {
        btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = label;
        self.live.textContent = err.message;
        var msg = document.createElement('p');
        msg.className = 'hmq-error'; msg.setAttribute('role', 'alert'); msg.textContent = err.message;
        btn.insertAdjacentElement('afterend', msg);
        setTimeout(function () { msg.remove(); }, 6000);
      });
  };

  function init() {
    document.querySelectorAll('[data-hair-match-quiz]').forEach(function (el) {
      if (el.__hmq) return;
      el.__hmq = new HairMatchQuiz(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})(typeof window !== 'undefined' ? window : globalThis);
